// src/components/journal/Calendar/WeeklyStats.tsx
import React from 'react';
import { DollarSign, CheckCircle } from 'lucide-react';

interface WeeklyStatsProps {
  stats: {
    totalTrades: number;
    totalPnl: number;
    winRate: number;
    planAdherenceRate: number;
    followedPlanCount: number;
    winningTrades: number;
    losingTrades: number;
  };
  mode: 'plan' | 'pnl';
  isMobile?: boolean;
}

export const WeeklyStats: React.FC<WeeklyStatsProps> = ({ stats, mode, isMobile = false }) => {
  // If no trades for the week, show empty state
  if (stats.totalTrades === 0) {
    return (
      <div className="h-full min-h-[80px] border-l border-gray-300/50 rounded-r-lg bg-white/30 backdrop-blur-sm flex flex-col items-center justify-center text-xs text-gray-500 px-1">
        <span>No trades</span>
        <span>this week</span>
      </div>
    );
  }

  // For mobile, use hard-coded color values with forced backgrounds
  if (isMobile) {
    // Determine background color based on performance
    let bgColor = '#ffffff'; // Default white
    let bgClass = 'weekly-stats-white';
    
    if (mode === 'plan') {
      if (stats.planAdherenceRate >= 70) {
        bgColor = '#10b981'; // emerald-500
        bgClass = 'weekly-stats-green';
      }
      else if (stats.planAdherenceRate >= 50) {
        bgColor = '#f59e0b'; // amber-500
        bgClass = 'weekly-stats-amber';
      }
      else {
        bgColor = '#ef4444'; // red-500
        bgClass = 'weekly-stats-red';
      }
    } else {
      if (stats.totalPnl > 0) {
        bgColor = '#10b981'; // emerald-500
        bgClass = 'weekly-stats-green';
      }
      else if (stats.totalPnl === 0) {
        bgColor = '#f59e0b'; // amber-500
        bgClass = 'weekly-stats-amber';
      }
      else {
        bgColor = '#ef4444'; // red-500
        bgClass = 'weekly-stats-red';
      }
    }
    
    return (
      <>
        {/* Add embedded style for aggressive overrides */}
        <style>
        {`
          .weekly-stats-white {
            background-color: #ffffff !important;
            background: #ffffff !important;
          }
          .weekly-stats-green {
            background-color: #10b981 !important;
            background: #10b981 !important;
          }
          .weekly-stats-amber {
            background-color: #f59e0b !important;
            background: #f59e0b !important;
          }
          .weekly-stats-red {
            background-color: #ef4444 !important;
            background: #ef4444 !important;
          }
          @media (max-width: 768px) {
            .weekly-stats-white {
              background-color: #ffffff !important;
              background: #ffffff !important;
            }
            .weekly-stats-green {
              background-color: #10b981 !important;
              background: #10b981 !important;
            }
            .weekly-stats-amber {
              background-color: #f59e0b !important;
              background: #f59e0b !important;
            }
            .weekly-stats-red {
              background-color: #ef4444 !important;
              background: #ef4444 !important;
            }
          }
        `}
        </style>
        <div 
          className={`h-full min-h-[80px] border-l border-gray-300 rounded-r-lg flex flex-col p-1.5 justify-between ${bgClass}`}
        >
          {/* Weekly header with total trade count */}
          <div className="text-xs font-medium text-white text-center mb-1 flex items-center justify-center">
            <span>{stats.totalTrades} {stats.totalTrades === 1 ? 'trade' : 'trades'}</span>
          </div>
          
          {/* Main stat based on active tab */}
          {mode === 'plan' ? (
            <div className="flex flex-col items-center justify-center">
              <div className="flex items-center mb-1">
                <CheckCircle size={12} className="text-white mr-0.5" />
                <span className="text-xs font-medium text-white">{stats.followedPlanCount}/{stats.totalTrades}</span>
              </div>
              <div className="text-xs font-bold text-white">
                {stats.planAdherenceRate.toFixed(0)}% <span className="font-normal">plan</span>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center">
              <div className="flex items-center mb-1">
                <DollarSign size={12} className="text-white mr-0.5" />
                <span className="text-xs font-medium text-white">
                  {stats.totalPnl >= 0 ? '+' : ''}{stats.totalPnl.toFixed(2)}
                </span>
              </div>
              <div className="text-xs font-bold text-white">
                {stats.winRate.toFixed(0)}% <span className="font-normal">win</span>
              </div>
            </div>
          )}
          
          {/* Secondary stat - always show W/L ratio */}
          <div className="text-[10px] text-white/80 text-center mt-1">
            <span>W/L: {stats.winningTrades}/{stats.losingTrades}</span>
          </div>
        </div>
      </>
    );
  }
  
  // For desktop, use the original styling
  const getBgColor = () => {
    if (mode === 'plan') {
      if (stats.planAdherenceRate >= 70) return 'bg-emerald-100/70';
      if (stats.planAdherenceRate >= 50) return 'bg-amber-100/70';
      return 'bg-red-100/70';
    } else {
      if (stats.totalPnl > 0) return 'bg-emerald-100/70';
      if (stats.totalPnl === 0) return 'bg-amber-100/70';
      return 'bg-red-100/70';
    }
  };

  return (
    <div 
      className={`h-full min-h-[80px] border-l border-gray-300/50 rounded-r-lg ${getBgColor()} backdrop-blur-sm flex flex-col p-1.5 justify-between`}
    >
      {/* Weekly header with total trade count */}
      <div className="text-xs font-medium text-gray-700 text-center mb-1 flex items-center justify-center">
        <span>{stats.totalTrades} {stats.totalTrades === 1 ? 'trade' : 'trades'}</span>
      </div>
      
      {/* Main stat based on active tab */}
      {mode === 'plan' ? (
        <div className="flex flex-col items-center justify-center">
          <div className="flex items-center mb-1">
            <CheckCircle size={12} className="text-primary-600 mr-0.5" />
            <span className="text-xs font-medium">{stats.followedPlanCount}/{stats.totalTrades}</span>
          </div>
          <div className="text-xs font-bold">
            {stats.planAdherenceRate.toFixed(0)}% <span className="font-normal">plan</span>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center">
          <div className="flex items-center mb-1">
            <DollarSign size={12} className={`${stats.totalPnl >= 0 ? 'text-emerald-600' : 'text-red-600'} mr-0.5`} />
            <span className={`text-xs font-medium ${stats.totalPnl >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {stats.totalPnl >= 0 ? '+' : ''}{stats.totalPnl.toFixed(2)}
            </span>
          </div>
          <div className="text-xs font-bold">
            {stats.winRate.toFixed(0)}% <span className="font-normal">win</span>
          </div>
        </div>
      )}
      
      {/* Secondary stat - always show W/L ratio */}
      <div className="text-[10px] text-gray-600 text-center mt-1">
        <span>W/L: {stats.winningTrades}/{stats.losingTrades}</span>
      </div>
    </div>
  );
};